import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Book } from '../../types';
import { getGraphQLClient, GET_BOOK } from '../../lib/graphql';
import BookFormTabs from './BookFormTabs';
import BookBasicsStep from './BookBasicsStep';
import DescriptionStep from './DescriptionStep';
import AudienceTab from './AudienceTab';
import CoverFrontMatterTab from './CoverFrontMatterTab';

export type EditTabId = 'about' | 'audience' | 'description' | 'cover';

interface BookEditFormProps {
  bookId: string;
  initialTab?: EditTabId;
  onComplete: (bookId: string) => void;
  onCancel: () => void;
  onTabChange?: (tab: EditTabId) => void;
}

export default function BookEditForm({
  bookId,
  initialTab = 'about',
  onComplete,
  onCancel,
  onTabChange,
}: BookEditFormProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<EditTabId>(initialTab);
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const client = getGraphQLClient();
    client
      .request<{ book: Book }>(GET_BOOK, { id: bookId })
      .then((data) => {
        if (data.book) {
          setBook(data.book);
        } else {
          setError('Book not found');
        }
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to load book');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bookId]);

  const handleTabChange = (tabId: string) => {
    const tab = tabId as EditTabId;
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const handleBookChange = (updatedBook: Book) => {
    setBook(updatedBook);
  };

  const handleBasicsComplete = (updatedBook: Book) => {
    setBook(updatedBook);
    handleTabChange('audience');
  };

  const handleAudienceNext = () => {
    handleTabChange('description');
  };

  const handleDescriptionSave = () => {
    handleTabChange('cover');
  };

  const handleDone = () => {
    if (book) {
      onComplete(book.id);
    }
  };

  const tabs = [
    {
      id: 'about',
      label: t('editBook.tabAbout', 'About'),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      id: 'audience',
      label: t('editBook.tabAudience', 'Audience'),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      id: 'description',
      label: t('editBook.tabDescription', 'Description'),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h7"
          />
        </svg>
      ),
    },
    {
      id: 'cover',
      label: t('editBook.tabCover', 'Cover & front matter'),
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4-4 4 4 8-8M4 6l16 0M4 6v14h16V6"
          />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="card p-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('common.loading')}</span>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error || !book) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <div className="card p-8">
          <div className="text-center py-8">
            <p className="text-red-600 dark:text-red-400 mb-4">{error || t('common.error')}</p>
            <button onClick={onCancel} className="btn-secondary">
              {t('common.back')}
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="card p-4 sm:p-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('books.edit')}
          </h1>
          <button
            onClick={handleDone}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="hidden sm:inline">{t('common.done')}</span>
          </button>
        </div>

        <BookFormTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

        <AnimatePresence mode="wait">
          {activeTab === 'about' && (
            <BookBasicsStep
              key="about"
              book={book}
              onComplete={handleBasicsComplete}
              onCancel={onCancel}
              isEditMode
            />
          )}

          {activeTab === 'audience' && (
            <AudienceTab
              key="audience"
              book={book}
              onBookChange={handleBookChange}
              onNext={handleAudienceNext}
            />
          )}

          {activeTab === 'description' && (
            <DescriptionStep
              key="description"
              book={book}
              onBookChange={handleBookChange}
              onNext={handleDescriptionSave}
              onBack={() => handleTabChange('audience')}
              isEditMode
            />
          )}

          {activeTab === 'cover' && <CoverFrontMatterTab key="cover" book={book} />}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
